import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CountryDropdown from '../components/CountryDropdown';
import { 
  User, Mail, Phone, Globe, Users, Calendar, 
  Send, CheckCircle, AlertCircle, Loader2, MessageSquare
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PartnerPublicLead = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [error, setError] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    destination_country: '',
    num_applicants: 1,
    travel_date: '',
    message: ''
  });

  useEffect(() => {
    fetchPartnerInfo();
  }, [slug]);

  const fetchPartnerInfo = async () => {
    try {
      const response = await axios.get(`${API}/api/partner-portal/public/${slug}/info`);
      setPartnerInfo(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching partner info:', err);
      if (err.response?.status === 404) {
        setError('This link is invalid or no longer active.');
      } else if (err.response?.status === 403) {
        setError('This link is currently unavailable.');
      } else {
        setError('Unable to load the form. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Phone number validation
    if (name === 'phone') {
      // Only allow digits
      const digitsOnly = value.replace(/\D/g, '');
      
      // Limit to 10 digits
      const limitedDigits = digitsOnly.slice(0, 10);
      
      setFormData(prev => ({ ...prev, phone: limitedDigits }));
      
      // Validate
      if (limitedDigits.length > 0 && limitedDigits.length < 10) {
        setPhoneError('Please enter 10 digits');
      } else {
        setPhoneError('');
      }
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!formData.phone || formData.phone.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    
    // Email validation (only if provided)
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }
    
    // destination_country already has the final value from CountryDropdown
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/api/partner-portal/public/${slug}/submit-lead`,
        formData
      );
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success('Your inquiry has been submitted!');
      } else if (response.data.is_duplicate) {
        setIsDuplicate(true);
        setDuplicateMessage(response.data.message);
        toast.info(response.data.message);
      }
    } catch (err) {
      console.error('Error submitting lead:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-slate-600 mx-auto" />
          <p className="mt-4 text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Link Unavailable</h1>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (isDuplicate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Already Registered</h1>
          <p className="text-slate-500 mb-4">
            {duplicateMessage || 'Your details are already in our system. We will contact you shortly.'}
          </p>
          <p className="text-sm text-slate-400">
            — {partnerInfo?.company_name}
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Thank You!</h1>
          <p className="text-slate-500 mb-4">
            Your inquiry has been received. We will contact you shortly.
          </p>
          <p className="text-sm text-slate-400">
            — {partnerInfo?.company_name}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      {/* Header - Partner Branding Only */}
      <header className="bg-white shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4 sm:py-5">
          <div className="flex items-center gap-3">
            {partnerInfo?.logo_url ? (
              <img 
                src={partnerInfo.logo_url} 
                alt={partnerInfo.company_name}
                className="h-10 sm:h-12 w-auto object-contain"
              />
            ) : (
              <div className="h-10 sm:h-12 w-10 sm:w-12 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg sm:text-xl">
                  {partnerInfo?.company_name?.charAt(0) || 'P'}
                </span>
              </div>
            )}
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-800">
                {partnerInfo?.company_name}
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Form Section */}
      <main className="max-w-2xl mx-auto px-4 py-6 sm:py-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-5 sm:px-8 py-6 text-white">
            <h2 className="text-lg sm:text-xl font-semibold mb-1">Get Started</h2>
            <p className="text-slate-300 text-sm">
              Fill in your details and we will assist you with your requirements.
            </p>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="p-5 sm:p-8 space-y-5">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Contact Information
              </h3>
              
              <div className="grid grid-cols-1 gap-4">
                {/* Name */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <User size={15} className="text-slate-400" />
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                    className="h-11"
                    data-testid="public-lead-name"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Phone size={15} className="text-slate-400" />
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="10-digit mobile number"
                      required
                      maxLength={10}
                      className={`h-11 ${phoneError ? 'border-red-300 focus:border-red-500' : ''}`}
                      data-testid="public-lead-phone"
                    />
                    {formData.phone.length > 0 && (
                      <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${formData.phone.length === 10 ? 'text-emerald-500' : 'text-slate-400'}`}>
                        {formData.phone.length}/10
                      </span>
                    )}
                  </div>
                  {phoneError && (
                    <p className="text-xs text-red-500">{phoneError}</p>
                  )}
                </div>

                {/* Email (Optional) */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Mail size={15} className="text-slate-400" />
                    Email Address <span className="text-slate-400 text-xs font-normal">(Optional)</span>
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="h-11"
                    data-testid="public-lead-email"
                  />
                </div>
              </div>
            </div>

            {/* Travel Details */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Requirements
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Service Type */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Globe size={15} className="text-slate-400" />
                    Service Required
                  </label>
                  <select
                    name="service_type"
                    value={formData.service_type}
                    onChange={handleChange}
                    className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-slate-400 focus:ring-2 focus:ring-slate-200 outline-none transition-all text-sm"
                    data-testid="public-lead-service"
                  >
                    <option value="">Select a service</option>
                    {partnerInfo?.services?.map(service => (
                      <option key={service.id} value={service.title}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Destination Country - Using Reusable CountryDropdown */}
                <CountryDropdown
                  value={formData.destination_country}
                  onChange={(country) => setFormData(prev => ({ ...prev, destination_country: country }))}
                  label="Destination"
                  placeholder="Select country"
                  showLabel={true}
                  size="default"
                />

                {/* Number of Applicants */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Users size={15} className="text-slate-400" />
                    Applicants
                  </label>
                  <select
                    name="num_applicants"
                    value={formData.num_applicants}
                    onChange={handleChange}
                    className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-slate-400 focus:ring-2 focus:ring-slate-200 outline-none transition-all text-sm"
                    data-testid="public-lead-applicants"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                    <option value={11}>More than 10</option>
                  </select>
                </div>

                {/* Travel Date */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Calendar size={15} className="text-slate-400" />
                    Travel Date
                  </label>
                  <Input
                    type="date"
                    name="travel_date"
                    value={formData.travel_date}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="h-11 text-sm"
                    data-testid="public-lead-date"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <MessageSquare size={15} className="text-slate-400" />
                  Additional Details <span className="text-slate-400 text-xs font-normal">(Optional)</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Any specific requirements..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-slate-400 focus:ring-2 focus:ring-slate-200 outline-none transition-all resize-none text-sm"
                  data-testid="public-lead-message"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <Button
                type="submit"
                disabled={submitting || formData.phone.length !== 10}
                className="w-full h-12 bg-slate-800 hover:bg-slate-900 text-white font-medium rounded-xl shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="public-lead-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Inquiry
                  </>
                )}
              </Button>
            </div>

            {/* Privacy Note */}
            <p className="text-center text-xs text-slate-400 pt-1">
              Your information is secure and will only be used to assist with your inquiry.
            </p>
          </form>
        </div>
      </main>

      {/* Minimal Footer - No external branding */}
      <footer className="py-4 text-center">
        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} {partnerInfo?.company_name}
        </p>
      </footer>
    </div>
  );
};

export default PartnerPublicLead;
